import { type ComponentType, type ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight, Minus, type LucideProps } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * StatTile — compact, non-decorated KPI tile used in dense stat rows.
 *
 * Use this anywhere a section needs "the 3-4 numbers that summarize this
 * area at a glance". Heavier `<KpiTile>` (with gradient + sparkline) stays
 * reserved for the dashboard hero row and per-page KPIs.
 *
 *   <StatTile label="Total audits" value={42} />
 *   <StatTile label="In progress" value={3} tone="info" />
 *   <StatTile label="Failed" value={1} tone="danger" hint="last 7 days" />
 *   <StatTile label="Avg score" value={78} trend={+4.2} />
 */

export type StatTone = 'default' | 'info' | 'success' | 'warning' | 'danger' | 'muted';

const TONE_CLASS: Record<StatTone, { value: string; chip: string }> = {
  default: { value: 'text-foreground', chip: 'bg-muted text-muted-foreground' },
  info: { value: 'text-info', chip: 'bg-info/10 text-info' },
  success: { value: 'text-success', chip: 'bg-success/10 text-success' },
  warning: { value: 'text-warning', chip: 'bg-warning/15 text-warning' },
  danger: {
    value: 'text-destructive',
    chip: 'bg-destructive/10 text-destructive',
  },
  muted: { value: 'text-muted-foreground', chip: 'bg-muted text-muted-foreground' },
};

export interface StatTileProps {
  label: ReactNode;
  value: ReactNode;
  /** Small subtitle/hint under the value. */
  hint?: ReactNode;
  /** Trend delta — positive = up, negative = down, null = no change. */
  trend?: number | null;
  /** Suffix for the trend, e.g. "vs last week". */
  trendSuffix?: string;
  /** Inverts the trend color (e.g. lower-is-better metrics). */
  trendInverted?: boolean;
  icon?: ComponentType<LucideProps>;
  tone?: StatTone;
  className?: string;
}

export function StatTile({
  label,
  value,
  hint,
  trend,
  trendSuffix = 'vs prev',
  trendInverted = false,
  icon: Icon,
  tone = 'default',
  className,
}: StatTileProps): JSX.Element {
  const t = TONE_CLASS[tone];
  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-card p-4 shadow-elevation-sm',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        {Icon ? (
          <span
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-md',
              t.chip,
            )}
            aria-hidden
          >
            <Icon className="h-3.5 w-3.5" />
          </span>
        ) : null}
      </div>
      <div
        className={cn(
          'mt-2 text-2xl font-semibold tabular leading-tight',
          t.value,
        )}
      >
        {value}
      </div>
      {hint || trend !== undefined ? (
        <div className="mt-1.5 flex items-center gap-2 text-xs">
          {trend !== undefined && trend !== null ? (
            <TrendChip value={trend} suffix={trendSuffix} inverted={trendInverted} />
          ) : null}
          {hint ? (
            <span className="truncate text-muted-foreground">{hint}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function TrendChip({
  value,
  suffix,
  inverted = false,
}: {
  value: number;
  suffix?: string;
  inverted?: boolean;
}): JSX.Element {
  if (!Number.isFinite(value) || value === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        <Minus className="h-3 w-3" />
        <span className="tabular">0%</span>
        {suffix ? <span className="text-muted-foreground/70">{suffix}</span> : null}
      </span>
    );
  }
  const positive = value > 0;
  const good = inverted ? !positive : positive;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-medium',
        good ? 'text-success' : 'text-destructive',
      )}
    >
      {positive ? (
        <ArrowUpRight className="h-3 w-3" />
      ) : (
        <ArrowDownRight className="h-3 w-3" />
      )}
      <span className="tabular">{Math.abs(value).toFixed(1)}%</span>
      {suffix ? <span className="text-muted-foreground/80">{suffix}</span> : null}
    </span>
  );
}
